import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import {
    Wifi, Gift, ExternalLink, Star, Users, BarChart3, TrendingUp,
    ArrowRight, Loader2, MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const HotelAnalytics = ({ organizationId, touchpointType }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { loadAnalytics(); }, [organizationId, touchpointType]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const params = {};
            if (organizationId && organizationId !== 'all') params.organization_id = organizationId;
            if (touchpointType && touchpointType !== 'all') params.touchpoint_type = touchpointType;
            const res = await api.get('/hotel/admin/analytics', { params });
            setData(res.data);
        } catch (e) { /* ignored */ } finally { setLoading(false); }
    };

    if (loading) {
        return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
    }

    if (!data || data.total_sessions === 0) {
        return (
            <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                    {t('hotel.noAnalytics', 'No hotel analytics data yet. Guest sessions will appear here.')}
                </CardContent>
            </Card>
        );
    }

    const pct = (n, total) => total > 0 ? Math.round((n / total) * 100) : 0;

    // Rating distribution for mini bar chart
    const maxDist = Math.max(...data.rating_distribution, 1);

    return (
        <div className="space-y-4" data-testid="hotel-analytics">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Conversion Funnel */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-amber-500" />
                            {t('hotel.conversionFunnel', 'Guest Conversion Funnel')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {data.conversion_funnel.map((step, i) => {
                                // Bars/percentages scale to the LARGEST stage so a
                                // partially backfilled event store can never produce
                                // impossible >100% values.
                                const base = Math.max(...data.conversion_funnel.map(s => s.count), 1);
                                const perc = pct(step.count, base);
                                // Distinct color per funnel stage (both rating bars stay gold)
                                const FUNNEL_COLORS = ['#8b5cf6', '#3b82f6', '#06b6d4', '#f59e0b', '#fbbf24', '#10b981', '#ec4899'];
                                return (
                                    <div key={step.step} className="flex items-center gap-3">
                                        <span className="text-xs text-muted-foreground w-28 shrink-0">{step.step}</span>
                                        <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${perc}%`,
                                                    backgroundColor: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
                                                }}
                                            />
                                        </div>
                                        <span className="text-xs font-medium w-14 text-right">
                                            {step.count} <span className="text-muted-foreground">({perc}%)</span>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* Outcome Breakdown */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-amber-500" />
                            {t('hotel.outcomeBreakdown', 'Guest Outcomes')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                <div className="flex items-center justify-center gap-1 mb-1">
                                    <Gift className="w-3.5 h-3.5 text-emerald-400" />
                                    <ExternalLink className="w-3.5 h-3.5 text-emerald-400" />
                                </div>
                                <p className="text-xl font-bold text-emerald-400">{data.both_count}</p>
                                <p className="text-xs text-muted-foreground">{t('hotel.giftAndPublicRating', 'Gift + Public Rating Clicked')}</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                <Gift className="w-4 h-4 text-amber-400 mx-auto mb-1" />
                                <p className="text-xl font-bold text-amber-400">{data.gift_only_count}</p>
                                <p className="text-xs text-muted-foreground">{t('hotel.giftOnly', 'Gift Only')}</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                <ExternalLink className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                                <p className="text-xl font-bold text-blue-400">{data.review_only_count}</p>
                                <p className="text-xs text-muted-foreground">{t('hotel.publicRatingClicked', 'Public Rating Clicked')}</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                                <MessageSquare className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                                <p className="text-xl font-bold text-purple-400">{data.private_feedback_count || 0}</p>
                                <p className="text-xs text-muted-foreground">{t('hotel.privateFeedback', 'Private Feedback')}</p>
                            </div>
                            <div className="col-span-2 text-center p-3 rounded-lg bg-slate-500/10 border border-slate-500/20">
                                <Users className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                                <p className="text-xl font-bold text-slate-400">{data.neither_count}</p>
                                <p className="text-xs text-muted-foreground">{t('hotel.neither', 'Neither')}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Rating Distribution + Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Star className="w-4 h-4 text-amber-500" />
                            {t('hotel.ratingDistribution', 'Rating Distribution')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {[5, 4, 3, 2, 1].map(star => {
                                const count = data.rating_distribution[star - 1] || 0;
                                const perc = pct(count, data.ratings_completed);
                                return (
                                    <div key={star} className="flex items-center gap-2">
                                        <span className="text-xs w-4 text-right text-muted-foreground">{star}</span>
                                        <Star className="w-3.5 h-3.5" fill="#FBBF24" stroke="#FBBF24" />
                                        <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-amber-400 rounded-full transition-all duration-500"
                                                style={{ width: `${perc}%` }}
                                            />
                                        </div>
                                        <span className="text-xs w-10 text-right">{count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <Wifi className="w-4 h-4 text-blue-500" />
                            {t('hotel.keyMetrics', 'Key Metrics')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">{t('hotel.emailCaptureRate', 'Email Capture Rate')}</span>
                                <span className="text-sm font-semibold">{pct(data.emails_captured, data.total_sessions)}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">{t('hotel.wifiDeliveryRate', 'Wi-Fi Delivery Rate')}</span>
                                <span className="text-sm font-semibold">{pct(data.wifi_sent, data.total_sessions)}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">{t('hotel.ratingCompletionRate', 'Rating Completion')}</span>
                                <span className="text-sm font-semibold">{pct(data.rated_sessions ?? data.ratings_completed, data.total_sessions)}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">{t('hotel.positiveRate', 'Positive Rating Rate')}</span>
                                <span className="text-sm font-semibold text-emerald-500">{pct(data.positive_ratings, data.ratings_completed)}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">{t('hotel.giftClaimRate', 'Gift Claim Rate')}</span>
                                <span className="text-sm font-semibold text-amber-500">{pct(data.gifts_claimed, data.positive_sessions ?? data.positive_ratings ?? 1)}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">{t('hotel.reviewClickRate', 'Public Rating Click Rate')}</span>
                                <span className="text-sm font-semibold text-blue-500">{pct(data.review_clicks, data.positive_sessions ?? data.positive_ratings ?? 1)}%</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default HotelAnalytics;
